import type { PlanType, PlanConfig } from "@/types/subscription";

/**
 * Catalogue des licences Synoplix — V5.5.
 *
 * 5 tiers actifs : FREE, STARTER, PRO, SMB, ENTERPRISE.
 * BETA est la clé technique — non modifiable, elle est persistée en base — du
 * palier d'ESSAI. Il s'affiche « Essai » côté interface (équivalent fonctionnel
 * de PRO sans facturation). La clé garde son nom d'origine parce qu'elle vit
 * dans l'enum Prisma `SubscriptionPlan` et dans la colonne `subscriptions.plan`
 * de chaque compte : la renommer imposerait une migration de données pour un
 * gain nul, le nom affiché étant la seule chose que voit un client.
 *
 * Sert désormais à faire essayer le produit AVANT la vente d'une licence.
 * ⚠ Aucune échéance automatique n'existe : une licence d'essai reste active
 * jusqu'à ce qu'un administrateur la change depuis /admin/users/[id].
 *
 * Modèle de tarification :
 *   - FREE / STARTER / PRO : B2C, facturation mensuelle ou annuelle (HT)
 *   - SMB / ENTERPRISE     : B2B, frais de souscription A1 + abonnement annuel A2+
 */
export const PLANS: Record<PlanType, PlanConfig> = {
  BETA: {
    name: "Essai",
    price: 0,
    priceYearly: 0,
    badge: "ESSAI",
    description: "Accès complet aux fonctionnalités, le temps d'essayer Synoplix avant l'achat.",
    limits: {
      projects: -1,
      pagesPerProject: 50,
      collaboratorsPerProject: 10,
      customSymbols: 200,
      storageGB: 10,
      versionsPerProject: 50,
    },
    features: {
      export: ["PDF", "PNG", "SVG", "JSON", "CSV"],
      templates: true,
      customSymbols: true,
      collaboration: true,
      comments: false,
      versionHistory: true,
      prioritySupport: true,
      api: false,
      sso: false,
      auditLog: false,
      nfC15100: true,
      cabinetBuilder: true,
      dxfImportExport: true,
      privateLibrary: false,
      whiteLabel: false,
      multiTeam: false,
      sla: false,
    },
  },

  FREE: {
    name: "Free",
    price: 0,
    priceYearly: 0,
    description: "Visualisation et édition de base pour découvrir Synoplix.",
    limits: {
      // Le comparatif V5 refuse la CRÉATION de projet à Free et Starter : ces
      // deux offres servent à intervenir sur le projet d'un autre, pas à en
      // ouvrir un. D'où 0, et non un petit nombre.
      projects: 0,
      pagesPerProject: 3,
      collaboratorsPerProject: 0,
      customSymbols: 0,
      storageGB: 1,
      versionsPerProject: 5,
    },
    features: {
      // « Formats d'export disponibles » est OUI sur les cinq offres dans le
      // comparatif V5 : ce n'est pas un levier de différenciation.
      export: ["PDF", "PNG", "SVG", "JSON", "CSV"],
      templates: false,
      customSymbols: false,
      collaboration: false,
      comments: false,
      versionHistory: false,
      prioritySupport: false,
      api: false,
      sso: false,
      auditLog: false,
      nfC15100: false,
      cabinetBuilder: false,
      dxfImportExport: false,
      privateLibrary: false,
      whiteLabel: false,
      multiTeam: false,
      sla: false,
    },
  },

  STARTER: {
    name: "Starter",
    // ⚠ DEUX prix, pas un. `price` est le mensuel SANS ENGAGEMENT ;
    // `priceYearly` est l'engagement d'un an, reglé en une fois.
    // 12 x 18 = 216, contre 180 : l'annuel economise exactement DEUX mois,
    // ce que le bandeau « 2 mois offerts » annonce. Tant que `price` valait 15
    // (l'equivalent mensuel de l'annuel), cette promesse etait fausse et le
    // tarif sans engagement n'existait nulle part.
    price: 18,
    priceYearly: 180, // = 15 €/mois avec engagement d'un an
    description: "Pour les indépendants — édition complète sur le terrain.",
    limits: {
      // Comme Free : Starter édite le projet d'un autre, il n'en crée pas.
      projects: 0,
      pagesPerProject: 10,
      collaboratorsPerProject: 2,
      customSymbols: 25,
      storageGB: 5,
      versionsPerProject: 20,
    },
    features: {
      export: ["PDF", "PNG", "SVG", "JSON", "CSV"],
      templates: true,
      customSymbols: true,
      collaboration: true,
      comments: false,
      versionHistory: true,
      prioritySupport: false,
      // Le comparatif V5 vend le plugin Vectorworks À PARTIR de Starter.
      api: true,
      sso: false,
      auditLog: false,
      // …et réserve les calculs normatifs à Pro. C'est l'argument principal du
      // palier Pro : l'ouvrir à Starter le brade cinq fois moins cher.
      nfC15100: false,
      cabinetBuilder: true,
      dxfImportExport: false,
      privateLibrary: false,
      whiteLabel: false,
      multiTeam: false,
      sla: false,
    },
  },

  PRO: {
    name: "Pro",
    // Meme regle que Starter : 12 x 90 = 1 080 contre 900, soit deux mois.
    price: 90,
    priceYearly: 900, // = 75 €/mois avec engagement d'un an
    badge: "POPULAIRE",
    highlight: true,
    description: "L'offre de référence pour les bureaux d'études et techniciens spectacle.",
    limits: {
      // 5 par MOIS dans le comparatif V5, pas 5 en tout. La fenêtre de
      // décompte vit dans `plan_capabilities` (quota.projects, window MONTH) ;
      // ce nombre-ci ne sert qu'à l'affichage de la grille publique.
      projects: 5,
      pagesPerProject: 50,
      collaboratorsPerProject: 10,
      customSymbols: 200,
      storageGB: 25,
      versionsPerProject: 50,
    },
    features: {
      export: ["PDF", "PNG", "SVG", "JSON", "CSV"],
      templates: true,
      customSymbols: true,
      collaboration: true,
      comments: false,
      versionHistory: true,
      prioritySupport: true,
      api: true,
      sso: false,
      auditLog: false,
      nfC15100: true,
      cabinetBuilder: true,
      dxfImportExport: true,
      // « Création de bibliothèques partagées » commence à SMB dans le
      // comparatif V5, pas à Pro.
      privateLibrary: false,
      whiteLabel: false,
      multiTeam: false,
      sla: false,
    },
  },

  SMB: {
    // Nom commercial des CGV-CGU v1.6 (« offre PME ») ; la clé `SMB` reste
    // celle de la base.
    name: "PME",
    price: 0,
    priceYearly: 1200, // A2+ par an
    // ⚠️ `setupFee` est la part LOGICIEL SEULE (achat ponctuel, année 1).
    // Le comparatif V5.5 annonce « 4 800 € la 1re année » = 3 600 de logiciel
    // + 1 200 de maintenance. Mettre 4 800 ici facturait la maintenance DEUX
    // fois, puisque le générateur de devis ajoute `yearlyFee` en ligne
    // récurrente : le client recevait un devis à 6 000 € au lieu de 4 800.
    setupFee: 3600, // A1 — logiciel seul (3 600 + 1 200 de maintenance = 4 800)
    yearlyFee: 1200, // A2+ (maintenance annuelle, due dès l'année 1)
    description: "PME — déploiement multi-utilisateur avec onboarding dédié.",
    limits: {
      // 50 par MOIS — cf. la remarque sur Pro plus haut.
      projects: 50,
      pagesPerProject: -1,
      collaboratorsPerProject: 25,
      customSymbols: -1,
      storageGB: 100,
      versionsPerProject: -1,
    },
    features: {
      export: ["PDF", "PNG", "SVG", "JSON", "CSV"],
      templates: true,
      customSymbols: true,
      collaboration: true,
      comments: false,
      versionHistory: true,
      prioritySupport: true,
      api: true,
      sso: false,
      auditLog: true,
      nfC15100: true,
      cabinetBuilder: true,
      dxfImportExport: true,
      privateLibrary: true,
      whiteLabel: true,
      multiTeam: false,
      // SLA opposable dès SMB dans le comparatif V5.5 (1 200 €/an).
      sla: true,
    },
  },

  ENTERPRISE: {
    name: "Entreprise",
    price: 0,
    priceYearly: 3600, // A2+ par an
    // Même règle que SMB : part LOGICIEL SEULE. Le comparatif annonce
    // « 14 600 € la 1re année » = 11 000 de logiciel + 3 600 de maintenance.
    setupFee: 11000, // A1 — logiciel seul (11 000 + 3 600 de maintenance = 14 600)
    yearlyFee: 3600, // A2+ (maintenance annuelle, due dès l'année 1)
    badge: "SUR DEVIS",
    // Pas de « déploiement on-premise » : les CGV-CGU v1.6 décrivent un service
    // en mode SaaS hébergé par OVHcloud (art. 1, 6 et 20). L'annoncer ici
    // promettait ce que le contrat ne prévoit pas.
    description: "Grands comptes — SSO, audit, SLA et support dédié.",
    limits: {
      projects: -1,
      pagesPerProject: -1,
      collaboratorsPerProject: -1,
      customSymbols: -1,
      storageGB: -1,
      versionsPerProject: -1,
    },
    features: {
      export: ["PDF", "PNG", "SVG", "JSON", "CSV"],
      templates: true,
      customSymbols: true,
      collaboration: true,
      comments: false,
      versionHistory: true,
      prioritySupport: true,
      api: true,
      sso: true,
      auditLog: true,
      nfC15100: true,
      cabinetBuilder: true,
      dxfImportExport: true,
      privateLibrary: true,
      whiteLabel: true,
      multiTeam: true,
      sla: true,
    },
  },
};

/** Ordre d'affichage public (grille de comparaison + pricing). */
export const PUBLIC_PLAN_ORDER: PlanType[] = ["FREE", "STARTER", "PRO", "SMB", "ENTERPRISE"];

/** Tier par défaut pour un nouveau compte (post-inscription, hors palier d'essai). */
export const DEFAULT_NEW_PLAN: PlanType = "FREE";

/**
 * Palier attribué À L'INSCRIPTION.
 *
 * ⚠️ C'EST LA LIGNE QUI OUVRE OU FERME LA BÊTA. Bêta CLOSE : vaut
 * `DEFAULT_NEW_PLAN` (= `FREE`), tout nouvel inscrit démarre au palier gratuit
 * et paie pour monter. Pour que chaque inscrit reçoive l'essai (« Essai » :
 * projets illimités, calculs normatifs, import DXF, tous les exports — l'équivalent
 * de PRO gratuitement), remettre `"BETA"` ici, l'unique endroit à changer.
 *
 * Les comptes DÉJÀ en `BETA` ne bougent pas quand on ferme : leur bascule est une
 * décision commerciale distincte, qui se pose compte par compte (dérogation
 * nominative datée, écran `/admin/users/[id]`, prend effet tout de suite et
 * expire seule).
 */
export const SIGNUP_PLAN: PlanType = DEFAULT_NEW_PLAN;

/**
 * Montant TTC au taux français de 20 %, formaté en français (« 21,60 € »).
 *
 * CGV-CGU v1.6, art. 13 et 40.2 : prix HT pour les professionnels, TTC pour les
 * consommateurs, « calculés au taux de TVA français de 20 % ». Le montant
 * réellement facturé reste celui de Stripe Tax, selon le pays de l'acheteur.
 */
export function ttcFR(montantHT: number): string {
  const v = Math.round(montantHT * 120) / 100;
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: Number.isInteger(v) ? 0 : 2,
    // Sans « always », le français ne groupe pas sous 10 000 : « 1080 € »,
    // là où les CGV-CGU écrivent « 1 080 € ».
    useGrouping: "always",
  } as Intl.NumberFormatOptions).format(v);
}
